import type {
  ConversationDetail,
  DismissalReason,
  MemoryKind,
  MemoryProposalView,
  ProjectMemoryStatus,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { csrf, csrfJson, done } from "../account/queries.ts";
import { api } from "../api.ts";
import { apiError, ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";

/** The caller's personal memories (openspec change memory-personal). */
export const memoryKeys = {
  all: ["memories"] as const,
  list: (q: string) => ["memories", "list", q] as const,
};

export function useMemories(q: string) {
  return useQuery({
    queryKey: memoryKeys.list(q),
    queryFn: async () =>
      ok(await api.api.memories.$get({ query: q.trim() ? { q: q.trim() } : {} })),
    placeholderData: (previous) => previous,
  });
}

/**
 * Downloads the caller's memories as a JSON file (memory-governance): the server names it; the
 * mutation resolves to that name.
 */
export function useMemoryExport() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.memories.export.$get();
      if (!res.ok) {
        throw await apiError(res);
      }
      const name =
        /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ??
        "memories.json";
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.append(link);
      link.click();
      link.remove();
      // Some browsers start the download after click() returns: revoke once it has begun.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return name;
    },
  });
}

/** Writes on the memory list; each refreshes every list query. */
export function useMemoryMutations() {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: memoryKeys.all });
  return {
    add: useMutation({
      mutationFn: async (json: { kind: MemoryKind; text: string }) =>
        ok(await api.api.memories.$post({ json }, csrf(csrfToken))),
      onSuccess: refresh,
    }),
    edit: useMutation({
      mutationFn: async (input: { id: string; text?: string; kind?: MemoryKind }) => {
        const { id, ...json } = input;
        return ok(
          await api.api.memories[":memoryId"].$patch(
            { param: { memoryId: id }, json },
            csrf(csrfToken),
          ),
        );
      },
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        done(
          await api.api.memories[":memoryId"].$delete({ param: { memoryId: id } }, csrf(csrfToken)),
        ),
      onSuccess: refresh,
    }),
    removeAll: useMutation({
      mutationFn: async () =>
        ok(await api.api.memories["delete-all"].$post(undefined, csrf(csrfToken))),
      onSuccess: refresh,
    }),
  };
}

/** Accepts (optionally edited) or dismisses a proposal shown under an answer. */
export function useProposalDecision(proposal: MemoryProposalView) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const param = { proposalId: proposal.id };
  // Decided: a cached conversation must not bring the card back when it is reopened.
  const forget = () =>
    queryClient.setQueriesData<ConversationDetail>({ queryKey: ["conversation"] }, (detail) =>
      detail?.memoryProposals
        ? {
            ...detail,
            memoryProposals: detail.memoryProposals.filter((p) => p.id !== proposal.id),
          }
        : detail,
    );
  return {
    accept: useMutation({
      mutationFn: async (text: string | undefined) =>
        ok(
          await api.api.memories.proposals[":proposalId"].accept.$post(
            { param, json: text === undefined ? {} : { text } },
            csrf(csrfToken),
          ),
        ),
      onSuccess: () => {
        forget();
        return queryClient.invalidateQueries({ queryKey: memoryKeys.all });
      },
    }),
    dismiss: useMutation({
      mutationFn: async () =>
        done(
          await api.api.memories.proposals[":proposalId"].dismiss.$post({ param }, csrf(csrfToken)),
        ),
      onSuccess: forget,
    }),
    /** The optional reason, after Dismiss (recommendation-outcomes). */
    reason: async (reason: DismissalReason) =>
      done(
        await api.api.memories.proposals[":proposalId"].dismiss.$post(
          { param },
          csrfJson(csrfToken, { reason }),
        ),
      ),
  };
}

// ── Project memory (openspec change memory-project) ───────────────────────────────────────

export const projectMemoryKeys = {
  all: (projectId: string) => ["project-memories", projectId] as const,
  list: (projectId: string, status: ProjectMemoryStatus, q: string) =>
    ["project-memories", projectId, status, q] as const,
};

export function useProjectMemories(
  projectId: string,
  status: ProjectMemoryStatus,
  q: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: projectMemoryKeys.list(projectId, status, q),
    queryFn: async () =>
      ok(
        await api.api.projects[":projectId"].memories.$get({
          param: { projectId },
          query: { status, ...(q.trim() && { q: q.trim() }) },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

/** Suggesting, deciding, withdrawing, editing and deleting; each refreshes the project's lists. */
export function useProjectMemoryMutations(projectId: string) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: projectMemoryKeys.all(projectId) }),
      queryClient.invalidateQueries({ queryKey: memoryKeys.all }),
    ]);
  const memory = (memoryId: string) => ({ param: { projectId, memoryId } });
  return {
    suggest: useMutation({
      mutationFn: async (json: { proposalId: string } | { memoryId: string }) =>
        ok(
          await api.api.projects[":projectId"].memories.$post(
            { param: { projectId }, json },
            csrf(csrfToken),
          ),
        ),
      onSuccess: refresh,
    }),
    decide: useMutation({
      mutationFn: async (input: { id: string; action: "approve" | "reject"; text?: string }) => {
        const json =
          input.action === "reject"
            ? { action: "reject" as const }
            : { action: "approve" as const, ...(input.text !== undefined && { text: input.text }) };
        const res = await api.api.projects[":projectId"].memories[":memoryId"].decision.$post(
          { ...memory(input.id), json },
          csrf(csrfToken),
        );
        return res.status === 204 ? done(res) : ok(res);
      },
      onSuccess: refresh,
    }),
    withdraw: useMutation({
      mutationFn: async (id: string) =>
        done(
          await api.api.projects[":projectId"].memories[":memoryId"].withdraw.$post(
            memory(id),
            csrf(csrfToken),
          ),
        ),
      onSuccess: refresh,
    }),
    edit: useMutation({
      mutationFn: async (input: { id: string; text: string }) =>
        ok(
          await api.api.projects[":projectId"].memories[":memoryId"].$patch(
            { ...memory(input.id), json: { text: input.text } },
            csrf(csrfToken),
          ),
        ),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        done(
          await api.api.projects[":projectId"].memories[":memoryId"].$delete(
            memory(id),
            csrf(csrfToken),
          ),
        ),
      onSuccess: refresh,
    }),
  };
}

/** Sends a proposal shown under an answer to its project instead of saving it personally. */
export function useProposalSuggestion(proposal: MemoryProposalView) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const projectId = proposal.origin?.projectId ?? "";
  return useMutation({
    mutationFn: async () =>
      ok(
        await api.api.projects[":projectId"].memories.$post(
          { param: { projectId }, json: { proposalId: proposal.id } },
          csrf(csrfToken),
        ),
      ),
    onSuccess: () => {
      // Consumed: a cached conversation must not bring the card back.
      queryClient.setQueriesData<ConversationDetail>({ queryKey: ["conversation"] }, (detail) =>
        detail?.memoryProposals
          ? {
              ...detail,
              memoryProposals: detail.memoryProposals.filter((p) => p.id !== proposal.id),
            }
          : detail,
      );
      return queryClient.invalidateQueries({ queryKey: projectMemoryKeys.all(projectId) });
    },
  });
}
